import React, { useState, useEffect } from 'react';
import { DocumentTextIcon, FunnelIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { activityLogService, ActivityLog, ActivityAction } from '../../services/activityLogService';
import CommonPageHeader from '../../components/common/CommonPageHeader';
import LoadingScreen from '../../components/common/LoadingScreen';
import Pagination from '../../components/common/Pagination';

const actionLabels: Record<ActivityAction, { label: string; color: string }> = {
  create: { label: 'Létrehozás', color: 'bg-green-100 text-green-700' },
  update: { label: 'Módosítás', color: 'bg-blue-100 text-blue-700' },
  delete: { label: 'Törlés', color: 'bg-red-100 text-red-700' },
  login: { label: 'Bejelentkezés', color: 'bg-emerald-100 text-emerald-700' },
  logout: { label: 'Kijelentkezés', color: 'bg-gray-100 text-gray-700' },
  login_failed: { label: 'Sikertelen login', color: 'bg-orange-100 text-orange-700' },
  lock: { label: 'Zárolás', color: 'bg-red-100 text-red-700' },
  unlock: { label: 'Feloldás', color: 'bg-green-100 text-green-700' },
  export: { label: 'Export', color: 'bg-purple-100 text-purple-700' },
  import: { label: 'Import', color: 'bg-indigo-100 text-indigo-700' },
};

const ActivityLogs = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const [filterAction, setFilterAction] = useState<ActivityAction | ''>('');
  const [filterResource, setFilterResource] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const loadLogs = async () => {
    setLoading(true);
    try {
      const filters: any = { limit: 500 };
      if (filterAction) filters.action = filterAction;
      if (filterResource) filters.resource = filterResource;
      if (filterDateFrom) filters.dateFrom = new Date(filterDateFrom);
      if (filterDateTo) {
        const d = new Date(filterDateTo);
        d.setHours(23, 59, 59);
        filters.dateTo = d;
      }
      const result = await activityLogService.getLogs(filters);
      setLogs(result);
    } catch (error) {
      console.error('Error loading logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const formatDate = (date: any) => {
    if (!date) return '';
    try {
      const d = date instanceof Date ? date : new Date(date);
      return d.toLocaleString('hu-HU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch {
      return '';
    }
  };

  const handleExport = () => {
    const headers = ['Időpont', 'Felhasználó', 'Email', 'Művelet', 'Erőforrás', 'Erőforrás ID', 'Részletek'];
    const rows = currentLogs.map(l => [
      formatDate(l.timestamp),
      l.userName || '',
      l.userEmail || '',
      actionLabels[l.action]?.label || l.action,
      l.resource || '',
      l.resourceId || '',
      l.details || ''
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity_logs_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <LoadingScreen />;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLogs = logs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(logs.length / itemsPerPage);

  return (
    <div className="space-y-6 pb-4">
      <CommonPageHeader
        title="Tevékenységi naplók"
        subtitle="Rendszer- és adatbázis-műveletek naplója"
        icon={DocumentTextIcon}
        showLastUpdate={false}
      >
        <button
          onClick={handleExport}
          className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors"
        >
          <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
          Export CSV
        </button>
      </CommonPageHeader>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <FunnelIcon className="h-5 w-5 text-gray-500" />
          <h3 className="text-sm font-medium text-gray-700">Szűrők</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Művelet</label>
            <select
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value as ActivityAction | '')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Összes</option>
              {Object.entries(actionLabels).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Erőforrás</label>
            <input
              type="text"
              value={filterResource}
              onChange={(e) => setFilterResource(e.target.value)}
              placeholder="pl. customers, contracts..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Dátumtól</label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Dátumig</label>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <button
            onClick={() => { setCurrentPage(1); loadLogs(); }}
            className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
          >
            Szűrés
          </button>
        </div>
      </div>

      {/* Desktop table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Időpont</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Felhasználó</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Művelet</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Erőforrás</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Részletek</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentLogs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    Nincs találat
                  </td>
                </tr>
              ) : (
                currentLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                      {formatDate(log.timestamp)}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-800">{log.userName || 'Ismeretlen'}</div>
                      <div className="text-xs text-gray-500">{log.userEmail}</div>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${actionLabels[log.action]?.color || 'bg-gray-100 text-gray-700'}`}>
                        {actionLabels[log.action]?.label || log.action}
                      </span>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                      <div>{log.resource}</div>
                      {log.resourceId && <div className="text-xs text-gray-400">{log.resourceId}</div>}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-600 max-w-xs truncate">
                      {log.details || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {currentLogs.length === 0 ? (
          <div className="bg-white p-4 rounded-lg text-center text-gray-500 border border-gray-200">
            Nincs találat
          </div>
        ) : (
          currentLogs.map((log) => (
            <div key={log.id} className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 space-y-2">
              <div className="flex justify-between items-start">
                <div>
                  <span className={`px-2 py-1 text-xs rounded-full ${actionLabels[log.action]?.color || 'bg-gray-100 text-gray-700'}`}>
                    {actionLabels[log.action]?.label || log.action}
                  </span>
                  <span className="ml-2 text-sm text-gray-700">{log.resource}</span>
                </div>
                <span className="text-xs text-gray-500">{formatDate(log.timestamp)}</span>
              </div>
              <div className="text-sm text-gray-700">{log.userName || 'Ismeretlen'}</div>
              <div className="text-xs text-gray-500">{log.userEmail}</div>
              {log.details && <div className="text-xs text-gray-600">{log.details}</div>}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {logs.length > 0 && (
        <div className="relative z-10">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={logs.length}
            indexOfFirstItem={indexOfFirstItem}
            indexOfLastItem={indexOfLastItem}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
          />
        </div>
      )}
    </div>
  );
};

export default ActivityLogs;
